import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import CategoryBlobCard from "@/components/CategoryBlobCard";

export default function CategoryProducts() {
  const { slug } = useParams();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [filters, setFilters] = useState({ brand: "", size: "", size_code: "", in_stock: false });
  const [brands, setBrands] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [sizeCodes, setSizeCodes] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    api.get("/categories").then(r => {
      setCat(r.data.find(c => c.slug === slug));
      setSubcategories(r.data.filter(c => c.parent_slug === slug));
    });
    setFilters({ brand: "", size: "", size_code: "", in_stock: false });
    api.get(`/products?category=${slug}&limit=500`).then(r => {
      setBrands([...new Set(r.data.map(p => p.brand).filter(Boolean))].sort());
      setSizes([...new Set(r.data.map(p => p.size).filter(Boolean))].sort());
      setSizeCodes([...new Set(r.data.map(p => p.size_code).filter(Boolean))].sort());
      setTotal(r.data.length);
    });
  }, [slug]);

  useEffect(() => {
    const params = new URLSearchParams({ category: slug });
    if (filters.brand) params.append("brand", filters.brand);
    if (filters.size) params.append("size", filters.size);
    if (filters.size_code) params.append("size_code", filters.size_code);
    if (filters.in_stock) params.append("in_stock", "true");
    api.get(`/products?${params}`).then(r => setItems(r.data));
  }, [slug, filters]);

  const hasActive = filters.brand || filters.size || filters.size_code || filters.in_stock;
  const resetFilters = () => setFilters({ brand: "", size: "", size_code: "", in_stock: false });

  // If category has subcategories → show subcategory grid instead of products
  if (subcategories.length > 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 text-sm text-neutral-400 mb-2">
          <Link to="/kategori" className="hover:text-[#FFD600]">Kategoritë</Link>
          <span>/</span>
          <span className="text-white">{cat?.name}</span>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl uppercase mb-8">{cat?.name || "Kategori"}</h1>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8 pb-4">
          {subcategories.map(c => (
            <CategoryBlobCard key={c.id} cat={c} testid={`subcat-card-${c.slug}`} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      {cat?.parent_slug && (
        <div className="flex items-center gap-2 text-sm text-neutral-400 mb-2">
          <Link to="/kategori" className="hover:text-[#FFD600]">Kategoritë</Link>
          <span>/</span>
          <Link to={`/kategori/${cat.parent_slug}`} className="hover:text-[#FFD600]">{cat.parent_slug}</Link>
          <span>/</span>
          <span className="text-white">{cat.name}</span>
        </div>
      )}
      <h1 className="font-display text-3xl sm:text-4xl uppercase mb-4">{cat?.name || "Produkte"}</h1>

      <div className="grid md:grid-cols-[240px_1fr] gap-6">
        {/* Filters */}
        <aside className="hg-card p-4 h-fit md:sticky md:top-20">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display text-xl uppercase">Filtro</div>
            {hasActive && (
              <button onClick={resetFilters} data-testid="filter-reset" className="text-[10px] uppercase tracking-wider text-neutral-400 hover:text-[#FFD600]">Pastro</button>
            )}
          </div>
          {total === 0 && (
            <div className="text-xs text-neutral-500 mb-3 p-2 bg-[#0B1220]/60 border border-[#1F2B44]">
              Kjo kategori nuk ka produkte të regjistruara ende.
            </div>
          )}
          <div className="space-y-4 text-sm">
            {brands.length > 0 && (
              <div>
                <label className="block text-xs uppercase text-neutral-400 mb-1">Marka</label>
                <select data-testid="filter-brand" value={filters.brand} onChange={e => setFilters({...filters, brand: e.target.value})}
                  className="w-full bg-[#0B1220] border border-[#1F2B44] px-3 py-2">
                  <option value="">Të gjitha</option>
                  {brands.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            )}
            {sizes.length > 0 && (
              <div>
                <label className="block text-xs uppercase text-neutral-400 mb-1">Diametri</label>
                <select data-testid="filter-size" value={filters.size} onChange={e => setFilters({...filters, size: e.target.value})}
                  className="w-full bg-[#0B1220] border border-[#1F2B44] px-3 py-2">
                  <option value="">Të gjitha</option>
                  {sizes.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            {sizeCodes.length > 0 && (
              <div>
                <label className="block text-xs uppercase text-neutral-400 mb-1">Modeli / Kodi</label>
                <select data-testid="filter-size-code" value={filters.size_code} onChange={e => setFilters({...filters, size_code: e.target.value})}
                  className="w-full bg-[#0B1220] border border-[#1F2B44] px-3 py-2">
                  <option value="">Të gjitha</option>
                  {sizeCodes.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" data-testid="filter-in-stock" checked={filters.in_stock} onChange={e => setFilters({...filters, in_stock: e.target.checked})}
                className="w-4 h-4 accent-[#FFD600]" />
              <span>Vetëm në stok</span>
            </label>
          </div>
        </aside>

        <div>
          <div className="text-sm text-neutral-400 mb-4">{items.length} produkte</div>
          {items.length === 0 ? (
            <div className="text-neutral-400 py-16 text-center">Nuk ka produkte për këto filtra.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              {items.map(p => <ProductCard key={p.id} p={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
